import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import accTickImage from '../assets/acc_tick_iat.png';
import accessLogoTickImage from '../assets/access_logo_tick_iat.png';
import { CustomerForInternalAdminTool } from '../types/CustomerForInternalAdminTool';
import InternalAdminToolService from "../services/InternalAdminToolService";
import CorporateSiteService from "../services/CorporateSiteService";
import styles from './InternalAdminTool.module.scss';

// Import our separate page components
import NoOrganisation from './NoOrganisation';
import InvalidOrganisation from './InvalidOrganisation';
import AlreadyConnectedView from './AlreadyConnectedView';
import SearchView from './SearchView';
import CreateCustomerView from './CreateCustomerView';
import {Alert, Confirm, Spinner} from "@tag/tag-components-react-v4";
import { ProjectAccents } from "../../General/config/projectAccents";

function InternalAdminTool() {
    const [searchParams] = useSearchParams();
    const organisationId = searchParams.get('organisationId');
    const evoEnvironment = searchParams.get('evoEnvironment');
    
    const [isValidId, setIsValidId] = useState<boolean>(true);
    const [searchPerformed, setSearchPerformed] = useState(false);
    const [foundCustomer, setFoundCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [searchedClientId, setSearchedClientId] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [corporateSite, setCorporateSite] = useState<string>('');
    const [accClient, setAccClient] = useState<string>('');
    const [alert, setAlert] = useState<{type: any, message: string}|undefined>();

    const [linkedCustomer, setLinkedCustomer] = useState<CustomerForInternalAdminTool | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isClientSearchLoading, setIsClientSearchLoading] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<'link' | 'create'>('link');

    useEffect(() => {
        initialLoad();
    }, [organisationId]);

    async function initialLoad() {
        setIsLoading(true);

        if (!organisationId || !evoEnvironment) {
            setIsLoading(false)
            return;
        }
        
        const site = await CorporateSiteService.getCorporateSiteByEvoEnvironmentName(evoEnvironment);
        setCorporateSite(site);
        
        const alreadyLinkedCustomer = await InternalAdminToolService.getClientByEvoOrganisationId(organisationId, site);
        if (alreadyLinkedCustomer?.clientId) {
            setLinkedCustomer(alreadyLinkedCustomer);
            setIsLoading(false)
            return;
        }

        const validCustomer = await InternalAdminToolService.checkUninitialisedACCOrganisationExists(organisationId, site);
        if (!validCustomer) {
            setIsValidId(false);
        }

        setIsLoading(false);
    }
    
    const handleLinking = async () => {
        setIsLoading(true);
        
        if (!selectedCustomer || !organisationId || !corporateSite) {
            setIsLoading(false);
            return;
        }

        try {
            const result = await InternalAdminToolService.hookClient(
                selectedCustomer.id,
                organisationId,
                corporateSite
            );

            if (!result?.hookSucceeded) {
                setAlert({ type: 'error', message: 'An error occurred while linking the client.' });
            } else {
                const items = result.provisioning ?? [];
                if (items.length === 0) {
                    setAlert({ type: 'success', message: 'Client linked successfully. No active templates to provision.' });
                } else {
                    const successes = items.filter(p => p.outcome === 'Success').length;
                    const failed = items.filter(p => p.outcome === 'Failed').length;
                    const skipped = items.filter(p => p.outcome === 'Skipped').length;

                    const lines = items.map(p =>
                        `· ${p.templateName ?? '(unknown)'} v${p.version ?? '?'}: ${p.outcome}${p.message ? ` — ${p.message}` : ''}`
                    );
                    const summary = `Client linked. Templates: ${successes} ok / ${skipped} skipped / ${failed} failed`;
                    const message = [summary, ...lines].join('\n');
                    const type: any = failed > 0 ? 'warning' : 'success';
                    setAlert({ type, message });
                }
            }
        } catch (error) {
            setAlert({type: 'error', message: 'An error occurred while linking the client.'});
        } finally {
            setIsClientSearchLoading(false);
            setSelectedCustomer(null);
            setIsLoading(false)
            initialLoad();
        }
    }

    const handleSearch = async (clientId: string) => {
        setIsClientSearchLoading(true);
        
        // If input is empty, reset search state
        if (!clientId.trim()) {
            setSearchPerformed(false);
            setFoundCustomer(null);
            setSearchedClientId('');
            setIsClientSearchLoading(false);
            return;
        }

        setSearchedClientId(clientId);
        setSearchPerformed(true);

        try {
            const customer = await InternalAdminToolService.getClientByClientIdForInternalAdminTool(parseInt(clientId), corporateSite);
            setFoundCustomer(customer?.clientId ? customer : null);
        } catch (error) {
            console.error('Error searching for client:', error);
            setFoundCustomer(null);
        } finally {
            setIsClientSearchLoading(false);
        }
    };
    
    let content = null;

    if (isLoading) {
        content = <Spinner full={true} />
    }
    else if (!organisationId || !evoEnvironment) {
        content = <NoOrganisation logoSrc={accTickImage} />;
    }
    else if (organisationId && !isValidId) {
        content = <InvalidOrganisation logoSrc={accTickImage} />;
    }
    // If we have a linked customer, show the AlreadyConnectedView
    else if (linkedCustomer) {
      return (
        <AlreadyConnectedView
          organisationId={organisationId}
          accTickImage={accTickImage}
          accessLogoTickImage={accessLogoTickImage}
          customer={linkedCustomer}
          corporateSite={corporateSite}
          setSelectedCustomer={setSelectedCustomer}
        />
      );
    }
    // Otherwise show tabs for link existing / create new
    else {
        content = (
            <div className={styles.container}>
                <div className={styles.tabBar}>
                    <button
                        className={`${styles.tab} ${activeTab === 'link' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('link')}
                    >
                        Link Existing Customer
                    </button>
                    <button
                        className={`${styles.tab} ${activeTab === 'create' ? styles.tabActive : ''}`}
                        onClick={() => setActiveTab('create')}
                    >
                        Create New Customer
                    </button>
                </div>
                <div className={styles.tabContent} style={{ display: activeTab === 'link' ? 'block' : 'none' }}>
                    <SearchView
                        organisationId={organisationId}
                        accTickImage={accTickImage}
                        accessLogoTickImage={accessLogoTickImage}
                        onSearch={handleSearch}
                        searchPerformed={searchPerformed}
                        foundCustomer={foundCustomer}
                        searchedClientId={searchedClientId}
                        loading={isClientSearchLoading}
                        accClient={accClient}
                        setAccClient={setAccClient}
                        corporateSite={corporateSite}
                        setSelectedCustomer={setSelectedCustomer}
                    />
                </div>
                <div className={styles.tabContent} style={{ display: activeTab === 'create' ? 'block' : 'none' }}>
                    <CreateCustomerView
                        organisationId={organisationId}
                        corporateSite={corporateSite}
                        onPipelineComplete={(result) => {
                            if (result === 'succeeded') {
                                initialLoad();
                            }
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <>
            {content}
            
            <Confirm
                visible={!!selectedCustomer}
                type="question"
                accent={ProjectAccents.CareCompliance}
                heading="Continue"
                onPrimaryButtonClick={() => {
                    handleLinking()
                }}
                onCancelButtonClick={() => {
                    setSelectedCustomer(null);
                }}
                onClose={() => setSelectedCustomer(null)}
            >
                <div>Are you sure you want to connect this ACC customer?</div>
                <br />
                <div><b>AN ERROR MAY RESULT IN A GDPR BREACH</b></div>
                <br />
                {selectedCustomer?.name} to {organisationId}
            </Confirm>

            {alert ?
                <Alert
                    className={styles.Alert}
                    closeTransition="fade"
                    type={alert.type}
                    showDuration={1200}
                    onClosed={() => setAlert(undefined)}
                >
                    {alert.message}
                </Alert> : undefined
            }
        </>
    );
}

export default InternalAdminTool;
